import React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { agentMarkdownHref, isAgentMarkdownPath } from '../../utils/agent/agentMarkdown';

const AGENT_ICON_SIZE = 16;

const Trigger = styled.span`
  position: relative;
  display: inline-flex;
`;

const Bubble = styled.span`
  position: absolute;
  z-index: 2;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.text.primary};
  color: ${({ theme }) => theme.palette.background.paper};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: var(--font-micro);
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px 5px 0;
    border-style: solid;
    border-color: ${({ theme }) => theme.palette.text.primary} transparent transparent;
  }

  ${Trigger}:hover &,
  ${Trigger}:focus-within & {
    opacity: 1;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.palette.text.secondary};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.accent.main};
    outline-offset: 3px;
  }
`;

const IconStack = styled.span`
  position: relative;
  display: block;
  width: ${AGENT_ICON_SIZE}px;
  height: ${AGENT_ICON_SIZE}px;

  svg {
    position: absolute;
    inset: 0;
  }

  svg:last-child {
    opacity: 0;
  }

  ${Button}:hover &,
  ${Button}:focus-visible & {
    svg:first-child {
      opacity: 0;
    }

    svg:last-child {
      opacity: 1;
    }
  }
`;

function AgentWindowIcon({ filled = false }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={AGENT_ICON_SIZE}
      height={AGENT_ICON_SIZE}
      aria-hidden
    >
      <rect
        x="1"
        y="1.5"
        width="14"
        height="13"
        rx="1.5"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path
        d="M1.6 5.2h12.8"
        stroke={filled ? 'var(--background-paper)' : 'currentColor'}
        strokeWidth="1.2"
      />
      {filled ? (
        <path
          d="M3.2 7.4h4.2M3.2 9.3h2.6M8.6 7.4h4.2M8.6 9.3h2.4"
          stroke="var(--background-paper)"
          strokeWidth="1.1"
          strokeLinecap="round"
        />
      ) : (
        <path
          d="M3.4 8h3.4M3.4 10.1h2.2M8.8 8h3.6M8.8 10.1h2.4"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

function ViewAsAgentButton() {
  const location = useLocation();
  if (isAgentMarkdownPath(location.pathname)) return null;

  const handleClick = () => {
    const current = new URL(window.location.href);
    current.pathname = location.pathname || '/';
    current.search = location.search || '';
    current.hash = location.hash || '';
    window.open(agentMarkdownHref(current.href), '_blank', 'noopener');
  };

  return (
    <Trigger>
      <Bubble aria-hidden>View as Agent</Bubble>
      <Button type="button" aria-label="View as Agent" onClick={handleClick}>
        <IconStack>
          <AgentWindowIcon />
          <AgentWindowIcon filled />
        </IconStack>
      </Button>
    </Trigger>
  );
}

export default ViewAsAgentButton;
